import { initData as sdkInitData } from "@telegram-apps/sdk-react";

import { decodeProfileToken } from "@/modules/shared/utils/profile-link";
import { crumb, logEx, logMsg } from "@/config/sentry-logger";

import type { ExtendedUpdateUserData } from "@/modules/shared/atoms/params-message/types";
type UpdateUserFn = (data: ExtendedUpdateUserData) => Promise<unknown>;

export const handleStartParam = async ({
  initData,
  updateUser,
  navigate,
  setUserData,
  setInitialized,
}: {
  initData: any;
  updateUser: UpdateUserFn;
  navigate: (path: string) => void;
  setUserData: (d: ExtendedUpdateUserData) => void;
  setInitialized: (b: boolean) => void;
}) => {
  try {
    try {
      sdkInitData.restore();
    } catch (error) {
      logMsg("sdkInitData.restore failed", "warning");
      logEx(error, "sdkInitData.restore");
    }

    const startParam = sdkInitData.startParam();
    const initDataChecksum = JSON.stringify(initData);
    const sessionKey = "startParamHandled";

    const alreadyHandled =
      sessionStorage.getItem(sessionKey) ===
      `${initDataChecksum}::${startParam}`;

    if (startParam && !alreadyHandled) {
      crumb("startParam detected", {
        hasStartParam: !!startParam,
        startParamLength: startParam.length,
      });

      const newUserData: ExtendedUpdateUserData = {};
      const segments = startParam.split("-");
      let matched = false;

      if (startParam.startsWith("profile-")) {
        const token = startParam.slice("profile-".length);

        try {
          const decoded = decodeProfileToken(token);
          crumb("Profile token decoded", {
            screen: decoded?.screen,
            hasUserId: !!decoded?.userId,
            hasReferrerId: !!decoded?.referrerId,
          });

          if (decoded.screen === 2 && decoded.userId) {
            if (decoded.referrerId) {
              const refData: ExtendedUpdateUserData = {
                referralCode: `rep_${decoded.referrerId}`,
              };
              try {
                await updateUser(refData);
              } catch (e) {
                logEx(e, "updateUser after profile decode (with userId)", {
                  refData,
                });
              }
            }

            sessionStorage.setItem(
              sessionKey,
              `${initDataChecksum}::${startParam}`,
            );
            setInitialized(true);
            crumb("Navigate to /profile/:userId", { userId: decoded.userId });
            navigate(`/profile/${decoded.userId}`);

            return;
          }

          if (decoded.screen === 2 && !decoded.userId && decoded.referrerId) {
            const refData: ExtendedUpdateUserData = {
              referralCode: `rep_${decoded.referrerId}`,
            };
            try {
              setUserData(refData);
              await updateUser(refData);
            } catch (e) {
              logEx(e, "updateUser after profile decode (no userId)", {
                refData,
              });
            }

            sessionStorage.setItem(
              sessionKey,
              `${initDataChecksum}::${startParam}`,
            );
            setInitialized(true);
            crumb("Navigate to /profile (no userId in token)", {
              referrerId: decoded.referrerId,
            });
            navigate(`/profile`);

            return;
          }
        } catch (e) {
          logMsg("Failed to decode profile token", "warning", {
            tokenLength: token.length,
          });
          logEx(e, "decodeProfileToken");
        }
      }

      segments.forEach((segment) => {
        if (segment.startsWith("rep_")) {
          newUserData.referralCode = segment;
          matched = true;

          return;
        }

        let parts: string[];

        if (segment.includes("=")) {
          parts = segment.split("=");
        } else if (segment.includes("_")) {
          parts = segment.split("_");
        } else {
          parts = [segment];
        }

        if (parts.length >= 2) {
          matched = true;
          const key = parts[0];
          const value = parts.slice(1).join(segment.includes("=") ? "=" : "_");

          if (key === "referral_type") {
            newUserData.registrationType = value;
          } else if (key === "from") {
            newUserData.from = value;
          }
        }
      });

      if (!matched) {
        newUserData.token = startParam;
      }

      try {
        setUserData(newUserData);
        await updateUser(newUserData);
        logMsg("updateUser success", "info", { ...newUserData });

        if (newUserData.from === "AURA") {
          sessionStorage.setItem(
            sessionKey,
            `${initDataChecksum}::${startParam}`,
          );
          setInitialized(true);
          crumb("Navigate to /achievements (from_AURA)");
          navigate("/achievements");

          return;
        }

        if (newUserData.from === "adsgram") {
          sessionStorage.setItem(
            sessionKey,
            `${initDataChecksum}::${startParam}`,
          );
          setInitialized(true);
          crumb("Navigate to /achievements (from_adsgram)");
          navigate("/achievements");

          return;
        }

        if (newUserData.from === "TACWALLET") {
          sessionStorage.setItem(
            sessionKey,
            `${initDataChecksum}::${startParam}`,
          );
          setInitialized(true);
          crumb("Navigate to /achievements (from_TACWALLET)");
          navigate("/achievements");

          return;
        }
      } catch (error) {
        logEx(error, "updateUser exception", {
          payloadKeys: Object.keys(newUserData),
        });
      }

      sessionStorage.setItem(sessionKey, `${initDataChecksum}::${startParam}`);
    } else if (startParam && alreadyHandled) {
      crumb("startParam already handled in session", {
        startParamLength: startParam.length,
      });
    }
  } catch (error) {
    logEx(error, "init useEffect fatal");
  }

  setInitialized(true);
};
